import React, { useEffect, useState } from 'react';
import { ChakraFabButton } from 'app/components/Core';
import { Flex } from '@chakra-ui/react';
import { ReactComponent as AddIcon } from 'resources/images/add.svg';
import { OverlayDialog } from 'app/components/Dialogs';
import { useAppDispatch, useAppSelector } from 'app/store';
import { fetchCreateReleaseValidationsEffect } from 'app/store/Releases/effects';
import { getCreateReleaseValidations } from 'app/store/Releases/selectors';
import {
  audioSelectScreenClosedAction,
  audioSelectScreenOpenedAction,
  tuneCoreAudioSelectScreenOpenedAction,
} from 'app/store/Mixpanel/slice';
import { ReleaseAudioSelectOverlay } from 'app/components/Releases/ReleaseAudioSelectOverlay';
import {
  LabelV2Release,
  ReleaseType,
  SoundCloudRelease,
  TuneCoreRelease,
} from 'types/releases';
import { SelectReleaseTypeOverlay } from './components/SelectReleaseTypeOverlay';

interface CreateReleaseOverlayProps {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
}

export type SelectedReleaseType =
  | 'selectType'
  | 'soundCloud'
  | 'tuneCore'
  | 'labelV2';

export const CreateReleaseOverlay: React.FC<CreateReleaseOverlayProps> = ({
  open,
  onOpen,
  onClose,
}) => {
  const [
    selectedReleaseType,
    setSelectedReleaseType,
  ] = useState<SelectedReleaseType>('selectType');

  const releaseValidation = useAppSelector(getCreateReleaseValidations);
  const dispatch = useAppDispatch();
  const isMultipleReleaseOptions =
    releaseValidation && releaseValidation?.type?.enum?.length > 1;

  useEffect(() => {
    if (!releaseValidation) {
      dispatch(fetchCreateReleaseValidationsEffect());
    }
  }, [dispatch, releaseValidation]);

  const handleClose = () => {
    onClose();
    dispatch(
      audioSelectScreenClosedAction({ releaseType: selectedReleaseType })
    );
    if (isMultipleReleaseOptions) {
      setSelectedReleaseType('selectType');
    }
  };

  const handleBack = () => {
    dispatch(
      audioSelectScreenClosedAction({ releaseType: selectedReleaseType })
    );
    setSelectedReleaseType('selectType');
  };

  const handleOpen = () => {
    onOpen();
    if (isMultipleReleaseOptions) {
      setSelectedReleaseType('selectType');
    }
  };

  const handleCreateSoundCloudRelease = () => {
    if (isMultipleReleaseOptions) {
      setSelectedReleaseType('soundCloud');
      dispatch(audioSelectScreenOpenedAction());
    }
  };

  const handleCreateTuneCoreRelease = () => {
    if (isMultipleReleaseOptions) {
      setSelectedReleaseType('tuneCore');
      dispatch(tuneCoreAudioSelectScreenOpenedAction());
    }
  };

  const overlayContent = (() => {
    switch (selectedReleaseType) {
      case 'tuneCore':
        return (
          <ReleaseAudioSelectOverlay
            onBack={isMultipleReleaseOptions ? handleBack : undefined}
            onClose={handleClose}
            release={{ type: ReleaseType.TUNECORE } as TuneCoreRelease}
          />
        );
      case 'soundCloud': {
        return (
          <ReleaseAudioSelectOverlay
            onBack={isMultipleReleaseOptions ? handleBack : undefined}
            onClose={handleClose}
            release={{ type: ReleaseType.SOUNDCLOUD } as SoundCloudRelease}
          />
        );
      }
      case 'labelV2':
        return (
          <ReleaseAudioSelectOverlay
            onBack={isMultipleReleaseOptions ? handleBack : undefined}
            onClose={handleClose}
            release={{ type: ReleaseType.LABEL_V2 } as LabelV2Release}
          />
        );
      case 'selectType':
      default:
        return (
          <SelectReleaseTypeOverlay
            onLabelV2ReleaseClick={() => setSelectedReleaseType('labelV2')}
            onSoundCloudReleaseClick={handleCreateSoundCloudRelease}
            onTuneCoreReleaseClick={handleCreateTuneCoreRelease}
            enabledReleaseTypes={releaseValidation?.type?.enum as ReleaseType[]}
          />
        );
    }
  })();

  return (
    <>
      <ChakraFabButton
        aria-label="create release"
        isDisabled={!releaseValidation}
        onClick={handleOpen}
        leftIcon={<AddIcon />}
      >
        New Release
      </ChakraFabButton>
      {open && (
        <OverlayDialog onClose={handleClose}>
          <Flex direction="column" height="100%">
            {overlayContent}
          </Flex>
        </OverlayDialog>
      )}
    </>
  );
};
